import classNames from "classnames";
import Link from "next/link";

import Image from "components/imageNew/image";
import { ChevronIcon } from "components/icons/icons";
import { EventCardProps } from "./types";
import { SponsorLogoAndText } from "./talkElements";
import { useMixpanelTracking } from "lib/hooks/useMixpanelTracking";
import { formatDateEventCard, FormatDateEventCardOptions } from "./utils";

type DateStringProps = {
  eventDateTime: Date;
  className?: string;
  options: FormatDateEventCardOptions;
};

const DateString = ({
  eventDateTime,
  className = "",
  options = {},
}: DateStringProps) => {
  return (
    <p
      data-testid="event-time"
      className={classNames(className, "font-bold text-[16px] text-mono-40")}
    >
      {formatDateEventCard(eventDateTime, options)}
    </p>
  );
};

export const EventCard = ({
  color = "cerise",
  chevronClasses = "",
  heading,
  title,
  image,
  sponsorImage,
  body,
  registration,
  eventDateTime,
  cardLink,
  variant,
  calendar,
  cardContainerClasses,
  timezone,
  duration,
  withDuration,
  dateComponentOverride,
  excerpt,
  sponsorDetails,
}: EventCardProps) => {
  const { track } = useMixpanelTracking();
  const handleCardClick = async () => {
    if (!cardLink) {
      return;
    }

    await track("Event card click", {
      "Click link": cardLink,
    });
  };

  const textContent = (
    <>
      <h3 className="mb-2 font-bold text-[22px] leading-[130%]">{title}</h3>
      {excerpt && (
        <p
          className={
            "mb-5 text-[16px] leading-[130%] text-mono-40 md:mb-3 lg:mb-5"
          }
        >
          {excerpt}
        </p>
      )}
      {body}
      <div
        data-testid="event-card__chevron"
        className={classNames(
          "pointer-events-none absolute right-0 top-0 flex h-full w-[34px] items-center justify-end text-[28px]",
          chevronClasses
        )}
      >
        <span
          className={
            "chevron relative right-[10px] text-black-rock transition-[right]"
          }
        >
          <ChevronIcon width={24} className={"-rotate-90"} />
        </span>
      </div>
    </>
  );

  const contentLinkClasses = classNames(
    "group relative block outline-none pr-[34px]",
    "focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-black-rock",
    "[&_.chevron]:group-hover:right-0 [&_.chevron]:group-focus-visible:right-0"
  );

  const cardContent = (
    <>
      <div
        className={classNames(`flex flex-col`, {
          "hidden lg:block": variant === "talk" || variant === "breakfast",
        })}
      >
        {cardLink ? (
          <Link
            href={cardLink}
            onClick={handleCardClick}
            className="block outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-black-rock"
          >
            <div className="pointer-events-none">
              {image && (
                <div
                  className={classNames("w-[210px]", {
                    "mx-auto w-[288px] max-w-full md:mx-[unset] md:w-[210px]":
                      variant === "subscriberEvent",
                  })}
                >
                  <Image
                    className={classNames(`h-[210px] w-full object-cover`, {
                      "h-auto md:h-[210px]": variant === "subscriberEvent",
                    })}
                    src={image.url}
                    width={image.width}
                    height={image.height}
                    sizes="min(400px, 100vw)"
                    alt={image.alt || `${title} event image`}
                    quality={75}
                    placeholder="blur"
                    blurDataURL={image.blurUpThumb || undefined}
                  />
                </div>
              )}
              {sponsorImage ? (
                <SponsorLogoAndText
                  image={sponsorImage}
                  sponsorDetails={sponsorDetails}
                />
              ) : null}
            </div>
          </Link>
        ) : (
          <div className="pointer-events-none">
            {image && (
              <div
                className={classNames("w-[210px]", {
                  "mx-auto w-[288px] max-w-full md:mx-[unset] md:w-[210px]":
                    variant === "subscriberEvent",
                })}
              >
                <Image
                  className={classNames(`h-[210px] w-full object-cover`, {
                    "h-auto md:h-[210px]": variant === "subscriberEvent",
                  })}
                  src={image.url}
                  width={image.width}
                  height={image.height}
                  sizes="min(400px, 100vw)"
                  alt={image.alt || `${title} event image`}
                  quality={75}
                  placeholder="blur"
                  blurDataURL={image.blurUpThumb || undefined}
                />
              </div>
            )}
            {sponsorImage ? (
              <SponsorLogoAndText
                image={sponsorImage}
                sponsorDetails={sponsorDetails}
              />
            ) : null}
          </div>
        )}
      </div>

      <div className="flex h-auto flex-auto flex-col justify-between">
        <div>
          <div className="mb-2 flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center">
                <p
                  className={classNames("font-bold text-[16px]", {
                    "text-cerise": color === "cerise",
                    "text-pro-blue": color === "pro-blue",
                    "hidden lg:block": variant === "subscriberEvent",
                  })}
                >
                  {heading}
                </p>

                <div className="hidden items-center md:flex">
                  <div
                    className={classNames("mx-2 text-mono-30", {
                      "hidden lg:block": variant === "subscriberEvent",
                    })}
                  >
                    •
                  </div>
                  {dateComponentOverride || (
                    <DateString
                      eventDateTime={eventDateTime}
                      options={{ timezone, duration, withDuration }}
                    />
                  )}
                </div>
              </div>
            </div>

            {calendar && (
              <div className="hidden shrink-0 md:block">{calendar}</div>
            )}
          </div>

          {cardLink ? (
            <Link
              href={cardLink}
              onClick={handleCardClick}
              className={contentLinkClasses}
            >
              {textContent}
            </Link>
          ) : (
            <div className="relative pr-[34px]">{textContent}</div>
          )}

          <div className="mt-3 flex items-center justify-between gap-4 md:hidden">
            {dateComponentOverride || (
              <DateString
                eventDateTime={eventDateTime}
                options={{ timezone, duration, withDuration }}
              />
            )}
            {calendar && <div className="shrink-0">{calendar}</div>}
          </div>
        </div>

        <div className="flex w-full justify-end self-end">{registration}</div>
      </div>
    </>
  );

  return (
    <div
      data-testid="event-card"
      className={classNames(`relative bg-white`, {
        "mx-auto max-md:max-w-[320px]": variant === "subscriberEvent",
      })}
      data-variant={variant}
    >
      <div
        data-testid="event-card__container"
        className={classNames(
          "flex justify-between gap-x-6 p-4",
          {
            "lg:min-h-[324px]": variant === "talk",
          },
          cardContainerClasses
        )}
      >
        {cardContent}
      </div>
    </div>
  );
};
